import { Injectable, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { map } from "rxjs/operators";
import { AppConfig } from "../../../app.config";
import { IPwdReset, IPwdChange, IPwdExpiredChange } from "../models/pwd-reset.model";
import { Observable, Subject, firstValueFrom } from "rxjs";
import { ICurrentUser, OrganizationalUnit, OrganizationalUnitConfig } from "../models";
import { Router } from "@angular/router";
import { KeyValuePair } from "../models/Generics/ikeyValuePair.model";
import { IIdProviderConfig } from "../models/IIdProviderConfig";
import { MatDialog } from "@angular/material/dialog";
import { TranslocoService } from '@ngneat/transloco';
import { clearBannerSession } from '../hu-signer-pro-banner/hu-signer-pro-banner.utils';

/**
* Funciones de los roles de candidatos: GESTIONAR CANDIDATO, GESTIONAR CANDIDATOS BASICO,
* PROMOVER CANDIDATOS y CANDIDATO PROMOCION INCOMPLETO.
*/
export const CANDIDATE_ROLES = ['CANDIDATEADMIN', 'ADMIN_CANDIDATE_BASIC', 'CANDIDATE_PROMOTE', 'CANDIDATE_PROMOTE_INCOMPLETE'];

@Injectable({
  providedIn: "root"
})
export class AuthService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  get url() { return AppConfig.settings.apiUrls.auth; }
  get urlCPP() { return AppConfig.settings.apiUrls.cpp; }
  identityChangedCallbacks = [];

  // access_token lives in memory only — not persisted in localStorage to reduce XSS attack surface.
  // refresh_token stays in localStorage for persistence across page reloads (SEC-001 partial fix).
  private _accessToken: string | null = null;
  private _refreshTimer: ReturnType<typeof setTimeout> | null = null;

  /** Emits when the session expires (refresh_token invalid/expired). UI layer subscribes to show message. */
  readonly sessionExpired$ = new Subject<void>();

  get httpOptions() {
    return {
      headers: AppConfig.settings.application.useClickjackingProteccion ? this.buildHttpHeader() : new HttpHeaders({
        "Content-Type": "application/x-www-form-urlencoded",
        Application: AppConfig.settings.application.code,
      })
    };
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private dialog: MatDialog,
  ) { }

  login(user, password, subdomain) {
    let clientId = "CPPA_ARA";
    if (subdomain && subdomain != "") {
      clientId = "CPPA_ARA;SUBDOMAIN";
      password = `{"pass":"${password}", "sub":"${subdomain}"}`;
    }

    const body = new HttpParams()
      .set("client_id", clientId)
      .set("client_secret", "SECRET")
      .set("grant_type", "password")
      .set("username", user)
      .set("password", encodeURIComponent(password));
    return this.http.post(`${this.url}/token`, body, this.httpOptions).pipe(
      map(res => {
        clearBannerSession();
        this.setSession(res, clientId);
        return res;
      })
    );
  }

  loginOTP(user, password, otp: string, subdomain: string) {
    let clientId = "CPPA_ARA;OTP";
    if (subdomain && subdomain != "") {
      clientId = "CPPA_ARA;OTP;SUBDOMAIN";
      password = `{"pass":"${password}", "sub":"${subdomain}", "otp":"${otp}"}`;
    } else {
      clientId = "CPPA_ARA;OTP";
      password = `{"pass":"${password}", "otp":"${otp}"}`;
    }

    const body = new HttpParams()
      .set("client_id", clientId)
      .set("client_secret", "SECRET")
      .set("grant_type", "password")
      .set("username", user)
      .set("password", encodeURIComponent(password));
    return this.http.post(`${this.url}/token`, body, this.httpOptions).pipe(
      map(res => {
        clearBannerSession();
        this.setSession(res, clientId);
        return res;
      })
    );
  }

  private clearLocalStorage(): void {
    localStorage.clear();
  }

  private static readonly SESSION_TOKEN_KEY = 'access_token';
  private static readonly SESSION_EXPIRES_KEY = 'token_expires_at';

  private setSession(authResult, refreshClient: string): void {
    this.clearLocalStorage();
    sessionStorage.removeItem(AuthService.SESSION_TOKEN_KEY);
    sessionStorage.removeItem(AuthService.SESSION_EXPIRES_KEY);

    this._accessToken = authResult.access_token;

    if (authResult.access_token && authResult.expires_in) {
      const expiresAt = Date.now() + parseInt(authResult.expires_in, 10) * 1000;
      sessionStorage.setItem(AuthService.SESSION_TOKEN_KEY, authResult.access_token);
      sessionStorage.setItem(AuthService.SESSION_EXPIRES_KEY, String(expiresAt));
      // MF backward-compat: microfrontends (dashboard, connect, leaves) read
      // access_token from localStorage. Writing here keeps them in sync without
      // requiring changes to the MF codebases.
      localStorage.setItem('access_token', authResult.access_token);
    }

    localStorage.setItem("roles", authResult.roles);
    localStorage.setItem("usersOu", authResult.usersOu);
    localStorage.setItem("organizationId", authResult.organizationId);
    localStorage.setItem("organizationName", authResult.appOrganizationName);
    localStorage.setItem("organizationDescription", authResult.appOrganizationDescription);
    localStorage.setItem("refresh_token", authResult.refresh_token);
    localStorage.setItem("userId", authResult.userId);
    localStorage.setItem("userNick", authResult.userNick);
    localStorage.setItem("userFirstname", authResult.userName);
    localStorage.setItem("userLastname", authResult.userLastname);
    localStorage.setItem("refreshClient", refreshClient);
    localStorage.setItem("isSamlActive", authResult.isSamlActive);
    localStorage.setItem("expires_in", authResult.expires_in);
    this.hasFiltersMetadatos().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      localStorage.setItem("hasAdjetivation", res.length > 0 ? "true" : "false");
      localStorage.setItem("hasFiltersMetadataEmployee", res.filter(x => x.personType == "EMPLEADO" && x.documentTypeId == null).length > 0 ? "true" : "false");
      localStorage.setItem("hasFiltersMetadataCandidate", res.filter(x => x.personType == "CANDIDATO" && x.documentTypeId == null).length > 0 ? "true" : "false");
      localStorage.setItem("hasFiltersDocumentsEmployee", res.filter(x => x.personType == "EMPLEADO" && x.documentTypeId != null).length > 0 ? "true" : "false");
      localStorage.setItem("hasFiltersDocumentsCandidate", res.filter(x => x.personType == "CANDIDATO" && x.documentTypeId != null).length > 0 ? "true" : "false");
      localStorage.setItem("hasFiltersMetadata", res.filter(x => x.documentTypeId == null).length > 0 ? "true" : "false");
      localStorage.setItem("hasFiltersDocuments", res.filter(x => x.documentTypeId != null && x.metadataId == null).length > 0 ? "true" : "false");
      localStorage.setItem("allAdjetivation", JSON.stringify(res));
    });
    this.hasFilterAdjetivation(0).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res =>{
       if(res.length > 0)
       {
         localStorage.setItem("allAdjetivationEmployee", JSON.stringify(true));
       }
       else
       {
         localStorage.setItem("allAdjetivationEmployee", JSON.stringify(false));
       }
    });
    if (authResult.isSamlActive && JSON.parse(authResult.isSamlActive)) {
      const returnUrl = authResult.samlReturnUrl.match("http[s]?://.*") ? authResult.samlReturnUrl : 'http://' + authResult.samlReturnUrl;
      localStorage.setItem("samlReturnUrl", returnUrl);
    }

    this.identityChanged();
    if (authResult.expires_in) {
      this.scheduleTokenRefresh(parseInt(authResult.expires_in, 10));
    }
  }

  private scheduleTokenRefresh(expiresInSeconds: number): void {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
    }
    // Trigger silent refresh 60 seconds before token expires
    const delay = Math.max(0, (expiresInSeconds - 60) * 1000);
    this._refreshTimer = setTimeout(() => {
      this.refreshToken().subscribe({
        error: () => {
          // refresh_token also expired — inform user and logout
          this.notifySessionExpired();
        }
      });
    }, delay);
  }

  notifySessionExpired(): void {
    this.sessionExpired$.next();
    setTimeout(() => this.logout(true), 3000);
  }

  logout(redirect = false) {
    const samlReturnUrl = this.getSamlReturnUrl();
    const isSamlActive = this.isSamlActive();

    this.dialog.closeAll();
    this._accessToken = null;
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = null;
    }
    sessionStorage.removeItem(AuthService.SESSION_TOKEN_KEY);
    sessionStorage.removeItem(AuthService.SESSION_EXPIRES_KEY);
    this.clearLocalStorage();
    clearBannerSession();
    this.identityChanged();

    if (redirect) {
      if (isSamlActive) {
        window.location.href = samlReturnUrl;
      } else {
        this.router.navigate(['login']);
      }
    }
  }

  refreshToken(): Observable<ICurrentUser> {
    // Reemplazo el access_token por el nuevo
    const client = localStorage.getItem("refreshClient") ? localStorage.getItem("refreshClient") : "CPPA_ARA";
    const body = new HttpParams()
      .set("client_id", client)
      .set("client_secret", "SECRET")
      .set("grant_type", "refresh_token")
      .set("refresh_token", localStorage.getItem("refresh_token"));
    return this.http
      .post<ICurrentUser>(`${this.url}/token`, body, this.httpOptions)
      .pipe(
        map(user => {
          if (user && user.access_token) {
            this.setSession(user, client);
          }

          return <ICurrentUser>user;
        })
      );
  }

  getUserRoles(): string {
    return localStorage.getItem("roles");
  }

  getOrganizationId(): string {
    return localStorage.getItem("organizationId");
  }

  getOrganizationName(): string {
    return localStorage.getItem('organizationName');
  }

  getOrganizationDescription(): string {
    return localStorage.getItem('organizationDescription');
  }

  getUserFirstName(): string {
    return localStorage.getItem('userFirstname');
  }

  getUserLastName(): string {
    return localStorage.getItem('userLastname');
  }

  getOrganizationalUnits(): OrganizationalUnit[] {
    const userOus = JSON.parse(localStorage.getItem("usersOu"));
    const organizationalUnits = [];

    for (let index = 0; index < userOus.length; index++) {
      const ou = userOus[index].organizationalUnit;

      if (ou) {
        organizationalUnits.push({
          id: ou.id,
          name: ou.name,
          isRoot: ou.isRoot,
          roles: userOus[index].roles
        });
      }
    }

    return organizationalUnits;
  }

  isInRole(rol: string): boolean {
    if (!localStorage.getItem("roles")) {
      return false;
    }
    const roles = localStorage.getItem("roles").split(",");

    return roles.indexOf(rol) >= 0;
  }

  getUserId(): string {
    return localStorage.getItem("userId");
  }

  getUserNick(): string {
    return localStorage.getItem("userNick");
  }

  isAuthenticated(): boolean {
    // refresh_token persists across page reloads; access_token lives in memory only.
    // If refresh_token is present, the interceptor will auto-renew access_token on first 401.
    return !!localStorage.getItem("refresh_token");
  }

  isSamlActive(): boolean {
    const isSamlActive = JSON.parse(localStorage.getItem('isSamlActive'));
    return isSamlActive;
  }

  isLicenseConfig(){
    return this.isInRole('LEAVECONFIG');
  }

  isRRHH() {
    return this.isInRole('RRHH_CONTENT');
  }

  RRHHManagment() {
    return this.isInRole('RRHH_ADMIN');
  }

  isAdministrator() {
    return this.isInRole('RRHH_ACCESS');
  }

  isLeaveManager(){
     return this.isInRole('LEAVEMANAGE');
  }

  isLeaveApprov(){
    return this.isInRole('LEAVEAPROV');
  }

  isLeaveConfig(){
    return this.isInRole('LEAVECONFIG');
 }

  employeeManagement() {
    return this.isInRole('MANAGE_EMPLOYEES');
  }

  isCandidateAdmin() {
    return this.isInRole('CANDIDATEADMIN');
  }

  isLSDSigner() {
    return this.isInRole('LAWBOOK SIGN');
  }

  isLSDWrite() {
    return this.isInRole('LAWBOOK WRITE');
  }

  isOverseer() {
    return this.isInRole('OVERSEER');
  }

  canEdit() {
    return this.isInRole('EMPLOYER EDIT');
  }

  isFirmante() {
    return this.isInRole('FIRMANTE');
  }

  isGestorDocumental() {
    return this.isInRole('RRHH_DOCUMENTS');
  }

  isCandidate() {
    return !this.isInRole('EMPLOYEE LD') && this.isInRole('CANDIDATE');
  }

  getSamlReturnUrl(): string {
    if (this.isSamlActive()) {
      return localStorage.getItem("samlReturnUrl");
    }
    return null;
  }

  getAccesToken(): string | null {
    if (this._accessToken) {
      return this._accessToken;
    }
    // Restore from sessionStorage if token hasn't expired yet (60s safety margin)
    const cached = sessionStorage.getItem(AuthService.SESSION_TOKEN_KEY);
    const expiresAt = parseInt(sessionStorage.getItem(AuthService.SESSION_EXPIRES_KEY) ?? '0', 10);
    if (cached && Date.now() < expiresAt - 60_000) {
      this._accessToken = cached;
      return this._accessToken;
    }
    return null;
  }

  isCandidateAdminBasic() {
    return this.isInRole('ADMIN_CANDIDATE_BASIC');
  }

  /** Gestión de candidatos, completa (CANDIDATEADMIN) o básica (ADMIN_CANDIDATE_BASIC). */
  hasCandidateAdminRole() {
    return this.isCandidateAdmin() || this.isCandidateAdminBasic();
  }

  forgotPassword(
    username: string,
    changePasswordCallbackUrl: string,
    appid: string,
    subdomain: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<any> {
    let params = new HttpParams();

    if (subdomain && subdomain != "") {
      appid = appid + ";SUBDOMAIN";
      username = username + ";" + subdomain;
    }
    params = params.append("userName", username);
    params = params.append("callbackUrl", changePasswordCallbackUrl);
    params = params.append("appId", appid);

    return this.http
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get<any>(`${this.url}/Account/ForgotPassword`, { params: params })
      .pipe(map(res => res));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resetPassword(pwd: IPwdReset): Observable<any> {
    const httpOpt = {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      })
    };

    const postData = JSON.stringify(pwd);
    return this.http
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .post<any>(`${this.url}/Account/ResetPassword`, postData, httpOpt)
      .pipe(map(res => res));
  }

  changePassword(pwd: IPwdChange) {
    const httpOpt = {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      })
    };

    const postData = JSON.stringify(pwd);
    return this.http
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .post<any>(`${this.url}/Account/ChangePassword`, postData, httpOpt)
      .pipe(map(res => res));
  }

  changeExpiredPassword(pwd: IPwdExpiredChange) {
    const httpOpt = {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      })
    };

    const postData = JSON.stringify(pwd);
    return this.http
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .post<any>(`${this.url}/password/change`, postData, httpOpt)
      .pipe(map(res => res));
  }

  onIdentityChanged(callback) {
    this.identityChangedCallbacks.push(callback);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async setUserOu(ou: string): Promise<any> {
    // si ya esta no lo cambio
    if (this.getOrganizationId() === ou) {
      return;
    }


    return await firstValueFrom(this.changeUserOu(ou))
      .then(async () => {
        return await firstValueFrom(this.refreshToken());
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async setUser(userId: string): Promise<any> {

    return await firstValueFrom(this.changeUser(userId))
      .then(async () => {
        return await firstValueFrom(this.refreshToken());
      });
  }

  async getTokenChangeApp(): Promise<string> {
    return await firstValueFrom(this.changeApp())
      .then(async (result) => {
        return result.token;
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAccess(): Observable<any[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.get<any[]>(`${this.url}/AppSwitch/GetAccess`);
  }

  hasAnyRole(userRoles, rolesToCheck) {
    return rolesToCheck.some(role => userRoles.includes(role));
  }

  private translateRoleLabel(key: string, fallback: string): string {
    const translated = this.transloco.translate(`roles.${key}`);
    return translated && translated !== `roles.${key}` ? translated : fallback;
  }

  translateText(key: string, fallback: string, params?: Record<string, unknown>): string {
    const translated = this.transloco.translate(key, params);
    return translated && translated !== key ? translated : fallback;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAppLinks(usersOus: any[], sameOuEmployee = false, sameOuEmployer = false) {
    let appLinks = [];
    const currentOu = this.getOrganizationId();

    if (usersOus.length > 0) {
      for (let index = 0; index < usersOus.length; index++) {
        const userOu = usersOus[index];

        if (this.isInRoleOu(userOu, ["PAYCHECK READ"])) {
          appLinks.push({
            link: `${AppConfig.settings.custom.cpp2016}/#/?t=`,
            description: `${this.translateText('common.goTo', 'Ir a')} Humanage 2016 RRHH ${userOu.organizationalUnit.name}`,
            roleName: 'RRHH',
            organizationalUnitId: userOu.organizationalUnit.id,
            userId: userOu.userId,
            humanageTheme: userOu.organizationalUnit.humanageTheme
          });
        }

        if (currentOu && userOu.organizationalUnit.id !== +currentOu || sameOuEmployer) {
          if (this.isInRoleOu(userOu, ["FIRMANTE"], ["RRHH_CONTENT", "RRHH_ACCESS"])) {
            appLinks.push({
              organizationalUnitId: userOu.organizationalUnit.id,
              module: "signer",
              description: `${this.translateRoleLabel('FIRMANTE', 'Firmante')} - ${userOu.organizationalUnit.name}`,
              roleName: 'FIRMANTE',
              ouName: userOu.organizationalUnit.name,
              userId: userOu.userId,
              humanageTheme: userOu.organizationalUnit.humanageTheme
            });
          }

          if (this.isInRoleOu(userOu, ["OVERSEER"], ["RRHH_CONTENT", "FIRMANTE", "RRHH_ACCESS", "RRHH_DOCUMENTS"])) {
            appLinks.push({
              organizationalUnitId: userOu.organizationalUnit.id,
              module: "overseer",
              description: `${this.translateRoleLabel('CONSULTA', 'Consulta')} - ${userOu.organizationalUnit.name}`,
              roleName: 'CONSULTA',
              ouName: userOu.organizationalUnit.name,
              userId: userOu.userId,
              humanageTheme: userOu.organizationalUnit.humanageTheme
            });
          }

          if (this.isInRoleOu(userOu, ["RRHH_CONTENT"])) {
            appLinks.push({
              organizationalUnitId: userOu.organizationalUnit.id,
              module: "employer",
              description: `${this.translateRoleLabel('RRHH', 'RRHH')} - ${userOu.organizationalUnit.name}`,
              roleName: 'RRHH',
              ouName: userOu.organizationalUnit.name,
              userId: userOu.userId,
              humanageTheme: userOu.organizationalUnit.humanageTheme
            });
          }

          if (this.isInRoleOu(userOu, ["RRHH_ACCESS"], ["RRHH_CONTENT"])) {
            appLinks.push({
              organizationalUnitId: userOu.organizationalUnit.id,
              module: "employer",
              description: `${this.translateRoleLabel('GESTIONAR ACCESOS', 'Administrador')} - ${userOu.organizationalUnit.name}`,
              roleName: 'GESTIONAR ACCESOS',
              ouName: userOu.organizationalUnit.name,
              userId: userOu.userId,
              humanageTheme: userOu.organizationalUnit.humanageTheme
            });
          }

          if (this.isInRoleOu(userOu, ["CANDIDATEADMIN", "ADMIN_CANDIDATE_BASIC"], ["RRHH_CONTENT", "RRHH_ACCESS"])) {
            appLinks.push({
              organizationalUnitId: userOu.organizationalUnit.id,
              module: "candidateadmin",
              description: `${this.translateRoleLabel('ADMINISTRADOR DE CANDIDATOS', 'Administrador de Candidatos')} - ${userOu.organizationalUnit.name}`,
              roleName: 'ADMINISTRADOR DE CANDIDATOS',
              ouName: userOu.organizationalUnit.name,
              userId: userOu.userId,
              humanageTheme: userOu.organizationalUnit.humanageTheme
            });
          }

          if (this.isInRoleOu(userOu, ["LEAVEAPROV"])) {
            appLinks.push({
              organizationalUnitId: userOu.organizationalUnit.id,
              module: "leaveaprov",
              description: `${this.translateRoleLabel('APROBADOR DE SOLICITUDES', 'Aprobador de Solicitudes')} - ${userOu.organizationalUnit.name}`,
              roleName: 'APROBADOR DE SOLICITUDES',
              ouName: userOu.organizationalUnit.name,
              userId: userOu.userId,
              humanageTheme: userOu.organizationalUnit.humanageTheme
            });
          }

          if (this.isInRoleOu(userOu, ["RRHH_DOCUMENTS"], ["RRHH_CONTENT", "RRHH_ACCESS"])) {
            appLinks.push({
              organizationalUnitId: userOu.organizationalUnit.id,
              module: "jefe",
              description: `${this.translateRoleLabel('GESTIONAR DOCUMENTOS', 'Gestionar Documentos')} - ${userOu.organizationalUnit.name}`,
              roleName: 'GESTIONAR DOCUMENTOS',
              ouName: userOu.organizationalUnit.name,
              userId: userOu.userId,
              humanageTheme: userOu.organizationalUnit.humanageTheme
            });
          }
        }

        if (currentOu && userOu.organizationalUnit.id !== +currentOu || sameOuEmployee) {
          if (this.isInRoleOu(userOu, ["EMPLOYEE_2016", "EMPLOYEE LD"])) {
            appLinks.push({
              organizationalUnitId: userOu.organizationalUnit.id,
              module: "employee",
              isLd: userOu.roles.some(x => x == "EMPLOYEE LD"),
              description: `${this.translateRoleLabel('EMPLEADO', 'Empleado')} - ${userOu.organizationalUnit.name}`,
              roleName: 'EMPLEADO',
              ouName: userOu.organizationalUnit.name,
              userId: userOu.userId,
              humanageTheme: userOu.organizationalUnit.humanageTheme
            });
          } else if (this.isInRoleOu(userOu, ["CANDIDATE"])) {
            appLinks.push({
              organizationalUnitId: userOu.organizationalUnit.id,
              module: "employee",
              isLd: true,
              description: `${this.translateRoleLabel('CANDIDATO', 'Candidato')} - ${userOu.organizationalUnit.name}`,
              roleName: 'CANDIDATO',
              ouName: userOu.organizationalUnit.name,
              userId: userOu.userId,
              humanageTheme: userOu.organizationalUnit.humanageTheme
            });
          }
        }

        if (userOu.applicationsWithRoles.some(x => x == "EDR")) {
          appLinks.push({
            link: `${AppConfig.settings.custom.edr}/#/?t=`,
            description: this.translateText('auth.authService.goToEdrLbl', `Ir a EDR de ${userOu.organizationalUnit.name}`, { ou: userOu.organizationalUnit.name }),
            roleName: 'RRHH',
            ouName: userOu.organizationalUnit.name,
            organizationalUnitId: userOu.organizationalUnit.id,
            userId: userOu.userId,
            humanageTheme: userOu.organizationalUnit.humanageTheme
          });
        }

        if (this.isInRoleOu(userOu, ["LAWBOOK READ"]) && !this.isInRoleOu(userOu,["LSD_HUMANAGE"])) {
          appLinks.push({
            link: `${AppConfig.settings.custom.cpp2016}/#/?t=`,
            description: this.translateText('auth.authService.goToLawBookLbl', `Ir a Libro Ley de ${userOu.organizationalUnit.name}`, { ou: userOu.organizationalUnit.name }),
            roleName: 'RRHH',
            ouName: userOu.organizationalUnit.name,
            organizationalUnitId: userOu.organizationalUnit.id,
            userId: userOu.userId,
            humanageTheme: userOu.organizationalUnit.humanageTheme
          });
        }
      }
    }
    appLinks = appLinks.sort((a, b) => a.description > b.description ? 1 : -1);
    return appLinks;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isInRoleOu(userOu: any, roles: string[], notIn: string[] = []): boolean {
    return userOu.roles.some(x => {
      let inRoles = false;
      roles.forEach(r => inRoles = inRoles || x == r);
      return inRoles;
    }) && (notIn.length < 1 || !userOu.roles.some(x => {
      let notInRoles = false;
      notIn.forEach(r => notInRoles = notInRoles || x == r);
      return notInRoles;
    }));
  }

  getUserOus(appId?: string): Observable<OrganizationalUnitConfig[]> {
    if (!appId) {
      appId = AppConfig.settings.application.id;
    }

    return this.http
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get<any>(
        `${this.url}/Logins/GetOuByApp/${appId}`
      )
      .pipe(
        map(res => {
          const ous: OrganizationalUnitConfig[] = [];
          for (const entity of res.value) {
            ous.push(
              new OrganizationalUnitConfig(
                entity.entityId,
                entity.entityDescription
              )
            );
          }
          return ous;
        })
      );
  }

  async anyRoleForApp(): Promise<boolean> {
    return await firstValueFrom(this.getUserOus(AppConfig.settings.custom.cpp2016AppId))
      .then(ous => {
        if (ous.length > 0) {
          return true;
        }
        return false;
      });
  }

  private identityChanged() {
    this.identityChangedCallbacks.forEach(callback => {
      callback();
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private changeUserOu = function (ou: string): Observable<any> {
    return this.http.put(`${this.url}/logins/SetOu/${ou}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private changeUser = function (userId: string): Observable<any> {
    return this.http.put(`${this.url}/logins/SetUser/${userId}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private changeApp = function (): Observable<any> {
    return this.http.post(`${this.url}/AppSwitch`);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  generateOTP(username: string, generateOTP: string, subdomain: string): Observable<any> {
    const params = {
      appId: AppConfig.settings.application.id,
      callbackUrl: generateOTP,
      userName: username,
      subdomain: subdomain
    };

    return this.http
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get<any>(`${this.url}/UserOtps/GenerateOTP`, { params: params })
      .pipe(map(res => res));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOtpQR = function (code: string): Observable<any> {
    const p = { code: code };

    return this.http.post(`${this.url}/UserOtps/QR`, p);
  };

  getSAML(ouId: number) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.get<any>(`${this.url}/saml/${ouId}`);
  }

  getIdProviderConfig(ouId: number) {
    return this.http.get<IIdProviderConfig>(`${this.url}/saml/GetSamlIdentityUserLogged/${ouId}`);
  }

  viewsAvailables(): KeyValuePair<string, string>[] {
    const views: KeyValuePair<string, string>[] = [];
    if (this.isCandidateAdmin() || this.isCandidateAdminBasic()) {
      views.push({ key: 'Candidatos', value: 'employer/candidate-find' });
    }
    if (this.isRRHH() || this.isAdministrator() || this.canEdit() || this.RRHHManagment() || this.isLeaveApprov()) {
      views.push({ key: 'Empleados', value: 'employer/employee-find' });
    }

    return views;
  }
  viewsAnalitycsAvailables(): KeyValuePair<string, string>[] {
    const views: KeyValuePair<string, string>[] = [];

    if (this.isRRHH()) {
      views.push({ key: 'Sets', value: 'employer/analytics-document-set' });
    }
    if (this.isRRHH()) {
      views.push({ key: 'Recibos de Sueldo', value: 'employer/analytics' });
    }
    return views;
  }

  hasFiltersMetadatos() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.get<any[]>(`${this.urlCPP}/Documentation/getAdjetivacion/`).pipe(map(res => res));
  }

  hasFilterAdjetivation(personType: number) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.get<any[]>(`${this.urlCPP}/Documentation/getAdjetivacion/${personType}`).pipe(map(res => res));
  }

  async createHuToken(): Promise<string> {
    return await firstValueFrom(this.huSwitches())
      .then(async (result) => {
        return result.token;
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  huSwitches = function (): Observable<any> {
    const parameters = {
      accessToken : this.getAccesToken(),
      expireIn : parseInt(localStorage.getItem("expires_in"))
    };
    return this.http.post(`${this.url}/AppSwitch/husigner`,parameters)
  };

  buildHttpHeader(){
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Application: AppConfig.settings.application.code,
    });
    return headers;
  }
}
